const STICKY_OFFSET = 160;

const SkeletonBlock = ({ className = '' }: { className?: string }) => (
  <div className={`bg-gray-200 rounded animate-pulse ${className}`} />
);

const NavItemSkeleton = () => (
  <div className="flex w-full items-center gap-3 rounded-lg px-3 py-2">
    <SkeletonBlock className="h-3.5 w-3.5 flex-shrink-0 rounded-full" />
    <SkeletonBlock className="h-3.5 w-40" />
  </div>
);

const ProviderCardSkeleton = ({ isTop = false }: { isTop?: boolean }) => (
  <div
    className={`rounded-xl border border-gray-200 bg-white ${isTop ? 'px-5 pb-5 pt-7' : 'p-5'}`}
  >
    <div className="flex gap-4">
      <SkeletonBlock className="h-12 w-12 flex-shrink-0 rounded-full" />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <SkeletonBlock className="h-4 w-40 mb-2" />
            <SkeletonBlock className="h-4 w-28 rounded-full" />
          </div>
          <SkeletonBlock className="h-4 w-10 flex-shrink-0" />
        </div>

        <SkeletonBlock className="mt-3 h-3.5 w-32" />

        <div className="mt-3 space-y-1.5">
          <SkeletonBlock className="h-3.5 w-full" />
          <SkeletonBlock className="h-3.5 w-[90%]" />
          <SkeletonBlock className="h-3.5 w-[75%]" />
        </div>

        <div className="mt-4 flex items-center gap-3">
          <SkeletonBlock className="h-8 w-24 rounded-lg" />
          <SkeletonBlock className="h-4 w-24" />
        </div>
      </div>
    </div>
  </div>
);

export const ProviderMatchingSkeleton = () => {
  return (
    <div className="px-3 py-3 animate-pulse">
      <div className="flex min-h-[600px] items-start rounded-2xl border border-gray-200 bg-white shadow-sm">

        {/* ── LEFT SIDEBAR ──────────────────────────────────────────────── */}
        <aside
          className="hidden w-[300px] flex-shrink-0 self-start rounded-l-2xl border-r border-gray-100 bg-white lg:flex lg:flex-col"
          style={{ position: 'sticky', top: STICKY_OFFSET, maxHeight: `calc(100vh - ${STICKY_OFFSET}px)` }}
        >
          {/* Progress header */}
          <div className="border-b border-gray-100 px-5 py-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <SkeletonBlock className="h-3.5 w-28" />
              <SkeletonBlock className="h-3.5 w-20" />
            </div>
            <SkeletonBlock className="h-1.5 w-full rounded-full" />
          </div>

          {/* Provider type nav */}
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <nav className="space-y-1">
              {[1, 2, 3, 4].map((i) => (
                <NavItemSkeleton key={i} />
              ))}
            </nav>
          </div>

          {/* What They Do */}
          <div className="border-t border-gray-100 px-4 py-4">
            <div className="rounded-lg bg-amber-50 px-4 py-3">
              <div className="mb-1.5 flex items-center gap-2">
                <SkeletonBlock className="h-3.5 w-3.5 rounded-full" />
                <SkeletonBlock className="h-3 w-24" />
              </div>
              <div className="space-y-1.5">
                <SkeletonBlock className="h-3 w-full" />
                <SkeletonBlock className="h-3 w-[95%]" />
                <SkeletonBlock className="h-3 w-[80%]" />
              </div>
            </div>
          </div>

          {/* Use Recommendation button */}
          <div className="border-t border-gray-100 px-4 py-4">
            <SkeletonBlock className="h-9 w-full rounded-lg" />
          </div>
        </aside>

        {/* ── RIGHT CONTENT ──────────────────────────────────────────────── */}
        <main className="min-w-0 flex-1 px-8 py-8">

          {/* Category header */}
          <div className="mb-6 border-b border-gray-100 pb-6">
            <SkeletonBlock className="h-8 w-72 mb-3" />
            <SkeletonBlock className="h-4 w-full mb-1.5" />
            <SkeletonBlock className="h-4 w-[85%]" />
          </div>

          {/* Recommendations heading */}
          <div className="mb-5">
            <SkeletonBlock className="h-6 w-52 mb-2" />
            <SkeletonBlock className="h-4 w-80" />
          </div>

          {/* Provider cards */}
          <div className="space-y-5">
            <ProviderCardSkeleton isTop />
            <ProviderCardSkeleton />
          </div>

          {/* Continue / Skip buttons */}
          <div className="mt-8 flex gap-3">
            <SkeletonBlock className="h-11 flex-1 rounded-xl" />
            <SkeletonBlock className="h-11 flex-1 rounded-xl" />
          </div>
        </main>
      </div>
    </div>
  );
};
